(function () {
  /**
   * @deprecated Please use src2/core/service/migration/migrationList.js
   *
   * @ngdoc factory
   * @name MigrationList
   * @description Model for a list of JIL organization migrations. Some types of migration are
   *   parsed into a custom model (e.g. T2EMigration) which extends Migration; others which lack
   *   additional response data and do not require helper functions are parsed into Migration.
   */
  angular.module('binky.core.migration').factory('MigrationList', factory);

  /* @ngInject */
  function factory(
    $q,
    _,
    binkySrc2,
    feature,
    jilOrganizationsMigrations,
    List,
    Migration,
    MIGRATION_LIST_CACHE_ID,
    MIGRATION_STATUS,
    modelCache,
    OptionsPerStatusMigration,
    T2EMigration
  ) {
    class MigrationList extends List {
      /**
       * @description Creates a new MigrationList
       *
       * @param {Object} options as described below
       * @param {String} [options.orgId] associates MigrationList instance with an org.
       * @param {String[]} [options.types] the migration types to fetch in this list
       */
      constructor(options) {
        super({
          cacheType: MIGRATION_LIST_CACHE_ID,
          // The classes to try parsing list items into. The base Migration class will parse any
          // type of migration JSON, so it is positioned last in the list.
          itemClassRef: [OptionsPerStatusMigration, T2EMigration, Migration],
          resource: jilOrganizationsMigrations.migrations,
        });
        this.orgId = options.orgId;
        this.types = options.types;
      }

      /**
       * @description Returns the first migration in the list whose type matches the specified type.
       *
       * @param {MIGRATION_TYPE} type the migration type to search for
       * @returns {Migration} the first migration with the specified type, or undefined if no such
       *  migration is found
       */
      findByType(type) {
        return _.find(this.items, {type});
      }

      /**
       * @description Retrieves the first migration found whose org is locked.
       *
       * @returns {Migration} the first migration found that indicates the org is locked. If
       *  no migration has a locked org, returns undefined.
       */
      getMigrationWithLockedOrg() {
        return this.items.find((item) => _.invoke(item, 'isOrgLocked'));
      }

      /**
       * @description Returns whether any migrations in the list are currently migrating.
       *
       * @returns {boolean} true if there is at least one migration with the status "MIGRATING",
       *  false otherwise
       */
      isMigrating() {
        return _.some(this.items, ['status', MIGRATION_STATUS.MIGRATING]);
      }

      key() {
        const params = getParams.call(this);
        return super.key(params);
      }

      /**
       * @description Refreshes the contents of the list
       *
       * @returns {Promise} a promise which is resolved when the list is refreshed successfully
       */
      refresh() {
        const params = getParams.call(this);
        return super.refresh(params);
      }

      /**
       * @description Check if the default behavior to add admins is allowed, assuming
       *  a migration has lifted its read-only state.
       *
       * @returns {Boolean} true if the default behavior to add admins is allowed.
       */
      shouldAlignWithAddAdminLogic() {
        return (
          // This check ensures that the default behavior is preserved in the case
          // that a certain type of migration does not have to lift the read-only state to
          // reach workflows of adding admins.
          !this.shouldForceAllowAddAdminsOrUsers() || this.shouldForceAllowAddAdmins()
        );
      }

      /**
       * @description Check if the default behavior to add users is allowed, assuming
       *  a migration has lifted its read-only state.
       *
       * @returns {Boolean} true if the default behavior to add users is allowed.
       */
      shouldAlignWithAddUserLogic() {
        return (
          // This check ensures that the default behavior is preserved in the case
          // that a certain type of migration does not have to lift the read-only state to
          // reach workflows of adding users.
          !this.shouldForceAllowAddAdminsOrUsers() || this.shouldForceAllowAddUsers()
        );
      }

      /**
       * @description Checks to see if any migration allows for
       *  adding admins, regardless of the usual requirements.
       *
       * @returns {Boolean} true if overriding existing behavior to add admins is allowed,
       *  returns false otherwise.
       */
      shouldForceAllowAddAdmins() {
        return _.some(this.items, (item) => _.invoke(item, 'shouldForceAllowAddAdmins'));
      }

      /**
       * @description Checks to see if any migration allows for
       *  adding admins or users, regardless of the usual requirements.
       *
       * @returns {Boolean} true if overriding existing behavior to add admins or users
       *  is allowed, returns false otherwise.
       */
      shouldForceAllowAddAdminsOrUsers() {
        return this.shouldForceAllowAddAdmins() || this.shouldForceAllowAddUsers();
      }

      /**
       * @description Checks to see if any migration allows for
       *  adding users, regardless of the usual requirements.
       *
       * @returns {Boolean} true if overriding existing behavior to add users is allowed,
       *  returns false otherwise.
       */
      shouldForceAllowAddUsers() {
        return _.some(this.items, (item) => _.invoke(item, 'shouldForceAllowAddUsers'));
      }

      /**
       * @description Checks to see if any migration allows for domain
       *  claiming, regardless of the usual requirements.
       *
       * @returns {Boolean} true if overriding existing behavior to claim domains is allowed,
       *  returns false otherwise.
       */
      shouldForceAllowDomainClaiming() {
        return _.some(this.items, (item) => _.invoke(item, 'shouldForceAllowDomainClaiming'));
      }

      /**
       * @description Checks to see if any migration blocks PA creation,
       *  regardless of the usual requirements.
       *
       * @returns {Boolean} true if overriding existing behavior to block PA creation,
       *  returns false otherwise.
       */
      shouldForceBlockPACreation() {
        return _.some(this.items, (item) => _.invoke(item, 'shouldForceBlockPACreation'));
      }

      /**
       * @description Fetches the list of organization migrations.
       *
       * @param {Object} options - options to pass to the MigrationList constructor
       * @returns {MigrationList} the MigrationList
       */
      static get(options) {
        const model = new MigrationList(options);
        const cachedModel = modelCache.get(MIGRATION_LIST_CACHE_ID, model.key());
        if (cachedModel) {
          return cachedModel;
        }
        model.refresh();
        return model;
      }
    }

    modelCache.register(MIGRATION_LIST_CACHE_ID, 10);

    class MigrationList2 extends binkySrc2.services.migration.MigrationList {
      /**
       * @description Method to refresh Migration list from back-end.
       * @returns {Promise} resolves to MigrationList on success, else rejects with error
       */
      refresh() {
        this.$resolved = false;
        this.$promise = $q((resolve, reject) => {
          super
            .refresh()
            .then(() => {
              resolve(this);
            })
            .catch((error) => reject(error.response))
            .finally(() => {
              this.$resolved = true;
            });
        });
        return this.$promise;
      }

      /**
       * @description Method to fetch the MigrationsList.
       *
       * @param {Object} options as described below
       * @param {String} options.orgId associates MigrationList instance with an org.
       * @param {String[]} options.types the migration types to fetch in this list
       * @return {MigrationList2} migrationList instance
       */
      static get(options) {
        const model = new MigrationList2(options);
        model.refresh();
        return model;
      }
    }

    //////////////

    /**
     * @description Returns the query params to be passed with HTTP GET requests when refreshing
     *   this MigrationList instance.
     *
     * @returns {Object} object with keys and values representing the query param names and values
     *   param value.
     */
    function getParams() {
      return _.omitBy(
        {
          orgId: this.orgId,
          types: _.join(this.types, ','),
        },
        _.isUndefined
      );
    }
    return feature.isEnabled('temp_react_port_migrations') ? MigrationList2 : MigrationList;
  }
})();
